import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePeopleAdmin } from '../context/PeopleAdminContext';
import { useUser } from 'hooks/useUser';
import {
  createRoleDefinition,
  updateRoleDefinition,
  deactivateRoleDefinition,
  reactivateRoleDefinition,
  setDefaultRoleDefinition
} from '../services/roleDefinitionService';

/**
 * Custom hook for managing role definition data and operations
 * @returns {Object} Role definition data, mutations, loading states, and utility functions
 */
export function useRoleDefinitions() {
  const queryClient = useQueryClient();
  const { clientId, roleDefinitions } = usePeopleAdmin();
  const { user } = useUser();

  // Create role definition mutation
  const createRoleMutation = useMutation({
    mutationFn: (roleData) => createRoleDefinition(
      { ...roleData, clientId }, 
      user?.id || 'system'
    ),
    onSuccess: async () => {
      console.log('useRoleDefinitions - Create successful, invalidating cache');
      
      // Invalidate and refetch to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['roleDefinitions', clientId] });
      await queryClient.refetchQueries({ 
        queryKey: ['roleDefinitions', clientId],
        type: 'active',
        exact: true 
      });
      
      console.log('useRoleDefinitions - Cache invalidated and refetched after create');
    },
  });

  // Update role definition mutation
  const updateRoleMutation = useMutation({
    mutationFn: ({ id, updates }) => {
      console.log('useRoleDefinitions - updateRoleMutation called with:', { id, updates });
      return updateRoleDefinition(id, updates);
    },
    onSuccess: async (data) => {
      console.log('useRoleDefinitions - Update successful:', data);
      console.log('useRoleDefinitions - Invalidating cache for clientId:', clientId);
      
      // Invalidate queries to mark them as stale
      await queryClient.invalidateQueries({ queryKey: ['roleDefinitions', clientId] });
      
      // Force refetch the query immediately
      await queryClient.refetchQueries({ 
        queryKey: ['roleDefinitions', clientId],
        type: 'active',
        exact: true 
      });
      
      console.log('useRoleDefinitions - Cache invalidated and refetched');
    },
    onError: (error) => {
      console.error('useRoleDefinitions - Failed to update role definition:', error);
    }
  });

  // Deactivate role definition mutation
  const deactivateRoleMutation = useMutation({
    mutationFn: (id) => deactivateRoleDefinition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roleDefinitions', clientId] });
    },
  });

  // Reactivate role definition mutation
  const reactivateRoleMutation = useMutation({
    mutationFn: (id) => reactivateRoleDefinition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roleDefinitions', clientId] });
    },
  });

  // Set default role mutation
  const setDefaultRoleMutation = useMutation({
    mutationFn: (id) => setDefaultRoleDefinition(id, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roleDefinitions', clientId] });
    },
  });

  // Get active role definitions - memoized
  const getActiveRoles = useCallback(() => roleDefinitions.filter(r => r.isActive !== false), [roleDefinitions]);

  // Get inactive role definitions - memoized
  const getInactiveRoles = useCallback(() => roleDefinitions.filter(r => r.isActive === false), [roleDefinitions]);

  // Get roles by assignment level - memoized
  const getRolesByAssignmentLevel = useCallback((assignmentLevel) => {
    return roleDefinitions.filter(r => r.assignmentLevel === assignmentLevel);
  }, [roleDefinitions]);

  // Get location roles - memoized
  const getLocationRoles = useCallback(() => getRolesByAssignmentLevel('LOCATION_LEVEL'), [getRolesByAssignmentLevel]);

  // Get org unit roles (all org levels) - memoized
  const getOrgUnitRoles = useCallback(() => {
    return roleDefinitions.filter(r => 
      r.assignmentLevel === 'ORGLEVEL_1' ||
      r.assignmentLevel === 'ORGLEVEL_2' ||
      r.assignmentLevel === 'ORGLEVEL_3'
    );
  }, [roleDefinitions]);

  // Get default role - memoized
  const getDefaultRole = useCallback(() => roleDefinitions.find(r => r.isDefault), [roleDefinitions]);

  // Get role by code - memoized
  const getRoleByCode = useCallback((roleCode) => {
    return roleDefinitions.find(r => r.roleCode === roleCode);
  }, [roleDefinitions]);

  // Get role by ID - memoized
  const getRoleById = useCallback((id) => {
    return roleDefinitions.find(r => r.id === id);
  }, [roleDefinitions]);

  // Helper function to convert OrgLevel to number for sorting
  const orgLevelToNumber = (orgLevel) => {
    switch (orgLevel) {
      case 'LOCATION_LEVEL': return 0;
      case 'ORGLEVEL_1': return 1;
      case 'ORGLEVEL_2': return 2;
      case 'ORGLEVEL_3': return 3;
      default: return 999;
    }
  };

  // Sort roles by hierarchy - memoized to prevent recreation
  const getSortedRoles = useCallback(() => {
    return [...roleDefinitions].sort((a, b) => {
      const levelA = orgLevelToNumber(a.assignmentLevel);
      const levelB = orgLevelToNumber(b.assignmentLevel);
      if (levelA !== levelB) {
        return levelA - levelB;
      }
      return (a.sortOrder || 999) - (b.sortOrder || 999);
    });
  }, [roleDefinitions]);

  return {
    roleDefinitions,
    
    // Mutations - use mutateAsync to return promises
    createRole: createRoleMutation.mutateAsync,
    updateRole: updateRoleMutation.mutateAsync,
    deactivateRole: deactivateRoleMutation.mutateAsync,
    reactivateRole: reactivateRoleMutation.mutateAsync,
    setDefaultRole: setDefaultRoleMutation.mutateAsync,
    
    // Loading states
    isCreating: createRoleMutation.isPending,
    isUpdating: updateRoleMutation.isPending,
    isDeactivating: deactivateRoleMutation.isPending,
    isReactivating: reactivateRoleMutation.isPending,
    isSettingDefault: setDefaultRoleMutation.isPending,
    
    // Utility functions
    getActiveRoles,
    getInactiveRoles,
    getRolesByAssignmentLevel,
    getLocationRoles,
    getOrgUnitRoles,
    getDefaultRole,
    getRoleByCode,
    getRoleById,
    getSortedRoles,
  };
}